import { useMemo, useState } from 'react'
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  NeutralPanel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  StrongText,
  Subheading,
  SurfaceCard,
  SurfaceRow,
} from '../../../components/ui'
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ClockIcon,
  VideoCameraIcon,
} from '@heroicons/react/20/solid'
import { AVAILABLE_DOCTORS, CONSULTATION_REASONS } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import type { ComponentProps, FormEvent } from 'react'
import type { ActionResult, Consultation, ConsultationDraft } from '../types'

type BadgeColor = ComponentProps<typeof Badge>['variant']

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

const INITIAL_FORM: ConsultationDraft = {
  doctorId: '',
  reason: '',
  timeSlot: '',
}

const TIME_SLOT_OPTIONS = ['9:00 AM', '10:30 AM', '1:00 PM', '2:00 PM', '3:30 PM']
const ALL_SPECIALTIES_OPTION = 'All specialties'

const DOCTOR_TIME_SLOT_MAP: Record<string, string[]> = {
  'doc-kim': ['9:00 AM', '10:30 AM', '1:00 PM'],
  'doc-patel': ['10:30 AM', '2:00 PM', '3:30 PM'],
  'doc-chen': ['9:00 AM'],
}

const SPECIALTY_COLORS: Record<string, { badge: BadgeColor; avatar: string }> = {
  'Internal Medicine': { badge: 'sky', avatar: 'bg-sky-600 text-white' },
  'Cardiology': { badge: 'rose', avatar: 'bg-rose-600 text-white' },
  'Endocrinology': { badge: 'violet', avatar: 'bg-violet-600 text-white' },
}

function doctorInitials(name: string): string {
  const parts = name.replace(/^Dr\.\s*/i, '').trim().split(/\s+/)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('').slice(0, 2)
}

function specialtyForDoctorId(doctorId: string): { badge: BadgeColor; avatar: string } | null {
  const doctor = AVAILABLE_DOCTORS.find((d) => d.id === doctorId)
  if (!doctor) return null
  return SPECIALTY_COLORS[doctor.specialty] ?? null
}

function doctorSupportsTimeSlot(doctorId: string, timeSlot: string): boolean {
  const normalizedTimeSlot = timeSlot.trim()
  if (!normalizedTimeSlot) {
    return true
  }

  const doctorSlots = DOCTOR_TIME_SLOT_MAP[doctorId]
  if (!doctorSlots) {
    return true
  }

  return doctorSlots.includes(normalizedTimeSlot)
}

interface ConsultationsTabProps {
  consultations: Consultation[]
  onBookConsultation: (draft: ConsultationDraft) => ActionResult
}

export default function ConsultationsTab({ consultations, onBookConsultation }: ConsultationsTabProps) {
  const [consultForm, setConsultForm] = useState<ConsultationDraft>(INITIAL_FORM)
  const [doctorTypeFilter, setDoctorTypeFilter] = useState<string>(ALL_SPECIALTIES_OPTION)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusTone, setStatusTone] = useState<'danger' | 'success'>('success')

  const upcoming = consultations.filter((consultation) => consultation.status === 'upcoming')
  const past = consultations.filter((consultation) => consultation.status === 'completed')

  const selectedDoctor = useMemo(
    () => AVAILABLE_DOCTORS.find((doctor) => doctor.id === consultForm.doctorId) ?? null,
    [consultForm.doctorId]
  )
  const doctorSpecialtyOptions = useMemo(
    () => Array.from(new Set(AVAILABLE_DOCTORS.map((doctor) => doctor.specialty))),
    []
  )
  const doctorsMatchingType = useMemo(
    () =>
      AVAILABLE_DOCTORS.filter(
        (doctor) =>
          doctorTypeFilter === ALL_SPECIALTIES_OPTION || doctor.specialty === doctorTypeFilter
      ),
    [doctorTypeFilter]
  )
  const doctorsMatchingTypeAndTime = useMemo(
    () =>
      doctorsMatchingType.filter((doctor) =>
        doctorSupportsTimeSlot(doctor.id, consultForm.timeSlot)
      ),
    [consultForm.timeSlot, doctorsMatchingType]
  )
  const availableDoctorsCount = doctorsMatchingTypeAndTime.filter((doctor) => doctor.available).length
  const selectedDoctorUnavailableReason = useMemo(() => {
    if (!selectedDoctor) {
      return null
    }
    if (!selectedDoctor.available) {
      return 'Selected doctor is currently unavailable.'
    }
    if (
      doctorTypeFilter !== ALL_SPECIALTIES_OPTION &&
      selectedDoctor.specialty !== doctorTypeFilter
    ) {
      return `Selected doctor does not match the selected type (${doctorTypeFilter}).`
    }
    if (!doctorSupportsTimeSlot(selectedDoctor.id, consultForm.timeSlot)) {
      return consultForm.timeSlot.trim()
        ? `Selected doctor is not available at ${consultForm.timeSlot}.`
        : 'Selected doctor has no matching available time.'
    }

    return null
  }, [consultForm.timeSlot, doctorTypeFilter, selectedDoctor])
  const selectedDoctorUnavailable = Boolean(selectedDoctorUnavailableReason)
  const isSubmitDisabled =
    !consultForm.doctorId.trim() ||
    !consultForm.reason.trim() ||
    !consultForm.timeSlot.trim() ||
    selectedDoctorUnavailable

  function selectDoctor(doctorId: string, suggestedSlot: string) {
    const doctor = AVAILABLE_DOCTORS.find((candidate) => candidate.id === doctorId)
    if (!doctor) {
      return
    }

    const matchesType =
      doctorTypeFilter === ALL_SPECIALTIES_OPTION || doctor.specialty === doctorTypeFilter
    const resolvedTimeSlot = consultForm.timeSlot || suggestedSlot
    const matchesTimeSlot = doctorSupportsTimeSlot(doctorId, resolvedTimeSlot)
    if (!doctor.available || !matchesType || !matchesTimeSlot) {
      return
    }

    setConsultForm((form) => ({
      ...form,
      doctorId,
      timeSlot: form.timeSlot || suggestedSlot,
    }))
    setStatusMessage(null)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatusMessage(null)

    const result = onBookConsultation(consultForm)
    if (!result.ok) {
      setStatusTone('danger')
      setStatusMessage(
        'error' in result
          ? result.error
          : '[CONSULT_BOOK_FAILED] Consultation booking failed.'
      )
      return
    }

    const bookedDoctor = AVAILABLE_DOCTORS.find((doctor) => doctor.id === consultForm.doctorId)
    setStatusTone('success')
    setStatusMessage(`Consultation booked${bookedDoctor ? ` with ${bookedDoctor.name}` : ''}.`)
    setConsultForm(INITIAL_FORM)
  }

  return (
    <div className="space-y-6">
      <section className="consultations-metrics-grid" aria-label="Consultation summary">
        <article className="consultations-metric-card">
          <p className={`consultations-metric-value ${TEXT_PRIMARY}`}>{upcoming.length}</p>
          <p className={`consultations-metric-label ${TEXT_SUBTLE}`}>Upcoming</p>
        </article>
        <article className="consultations-metric-card">
          <p className={`consultations-metric-value ${TEXT_PRIMARY}`}>{past.length}</p>
          <p className={`consultations-metric-label ${TEXT_SUBTLE}`}>Completed</p>
        </article>
        <article className="consultations-metric-card">
          <p className={`consultations-metric-value ${TEXT_PRIMARY}`}>{availableDoctorsCount}</p>
          <p className={`consultations-metric-label ${TEXT_SUBTLE}`}>Providers available</p>
        </article>
      </section>

      <section className="consultations-overview-grid">
        <SurfaceCard as="article">
          <div className="flex items-start gap-3">
            <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
              <CalendarDaysIcon className="size-5" />
            </span>
            <div>
          <Subheading variant="dashboard">Book a consultation</Subheading>
              <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>
                Availability depends on doctor type and your selected time.
              </p>
            </div>
          </div>

          {selectedDoctor ? (
            <NeutralPanel className="mt-4 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <Avatar
                  initials={doctorInitials(selectedDoctor.name)}
                  className={`size-9 ${
                    SPECIALTY_COLORS[selectedDoctor.specialty]?.avatar ?? 'bg-zinc-600 text-white'
                  }`}
                />
                <div className="min-w-0">
                  <StrongText>{selectedDoctor.name}</StrongText>
                  <p className={`text-xs/5 ${TEXT_SUBTLE}`}>
                    {selectedDoctor.specialty} · Next slot {selectedDoctor.nextSlot}
                  </p>
                </div>
              </div>
            </NeutralPanel>
          ) : (
            <p className={`mt-4 text-xs/5 ${TEXT_FAINT}`}>Select an available provider from the list.</p>
          )}

          <form onSubmit={handleSubmit} className="mt-5">
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Doctor type</Label>
                  <Select
                    value={doctorTypeFilter}
                    onValueChange={(nextValue) => {
                      setDoctorTypeFilter(nextValue)
                      setStatusMessage(null)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_SPECIALTIES_OPTION}>{ALL_SPECIALTIES_OPTION}</SelectItem>
                      {doctorSpecialtyOptions.map((specialty) => (
                        <SelectItem key={specialty} value={specialty}>
                          {specialty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <Label>Doctor</Label>
                  <Select
                    value={consultForm.doctorId || undefined}
                    onValueChange={(doctorId) => {
                      const doctor = AVAILABLE_DOCTORS.find((candidate) => candidate.id === doctorId)
                      selectDoctor(doctorId, doctor?.nextSlot ?? '')
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctorsMatchingType.map((doctor) => {
                        const matchesTimeSlot = doctorSupportsTimeSlot(
                          doctor.id,
                          consultForm.timeSlot
                        )
                        const bookable = doctor.available && matchesTimeSlot
                        const label = `${doctor.name} — ${doctor.specialty}${
                          !doctor.available
                            ? ' (Unavailable)'
                            : !matchesTimeSlot && consultForm.timeSlot
                              ? ` (Not available at ${consultForm.timeSlot})`
                              : ''
                        }`

                        return (
                          <SelectItem key={doctor.id} value={doctor.id} disabled={!bookable}>
                            {label}
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <Label>Reason for visit</Label>
                  <Select
                    value={consultForm.reason || undefined}
                    onValueChange={(nextValue) => {
                      setConsultForm((form) => ({ ...form, reason: nextValue }))
                      setStatusMessage(null)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSULTATION_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <Label>Preferred time</Label>
                  <Select
                    value={consultForm.timeSlot || undefined}
                    onValueChange={(nextValue) => {
                      setConsultForm((form) => ({ ...form, timeSlot: nextValue }))
                      setStatusMessage(null)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOT_OPTIONS.map((timeSlot) => (
                        <SelectItem key={timeSlot} value={timeSlot}>
                          {timeSlot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="pt-2">
                  <Button type="submit" className="w-full" disabled={isSubmitDisabled}>
                    <CalendarDaysIcon data-slot="icon" />
                    Book consultation
                  </Button>
                </div>
                {selectedDoctorUnavailable && selectedDoctorUnavailableReason && (
                  <StatusText>{selectedDoctorUnavailableReason}</StatusText>
                )}
                {statusMessage && (
                  <StatusText tone={statusTone === 'danger' ? 'danger' : 'success'} aria-live="polite">
                    {statusMessage}
                  </StatusText>
                )}
              </FieldGroup>
            </Fieldset>
          </form>
        </SurfaceCard>

        <SurfaceCard as="article">
          <div className="flex items-center justify-between gap-3">
            <Subheading variant="dashboard">Quick provider pick</Subheading>
            <Badge variant="zinc">
              {availableDoctorsCount} available
            </Badge>
          </div>
          <p className={`mt-1 text-xs/5 ${TEXT_FAINT}`}>
            Showing providers for <span className={TEXT_SUBTLE}>{doctorTypeFilter}</span>
            {consultForm.timeSlot ? (
              <>
                {' '}
                at <span className={TEXT_SUBTLE}>{consultForm.timeSlot}</span>
              </>
            ) : null}
            .
          </p>

          <div className="consultations-doctor-list mt-4">
            {doctorsMatchingType.length > 0 ? (
              doctorsMatchingType.map((doctor) => {
                const isSelected = consultForm.doctorId === doctor.id
                const colors = SPECIALTY_COLORS[doctor.specialty]
                const matchesTimeSlot = doctorSupportsTimeSlot(doctor.id, consultForm.timeSlot)
                const bookable = doctor.available && matchesTimeSlot
                return (
                  <Button
                    key={doctor.id}
                    type="button"
                    variant="unstyled"
                    className={`consultations-doctor-button ${isSelected ? 'consultations-doctor-button-selected' : ''}`}
                    onClick={() => selectDoctor(doctor.id, doctor.nextSlot)}
                    disabled={!bookable}
                    aria-pressed={isSelected}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        initials={doctorInitials(doctor.name)}
                        className={`size-9 ${colors?.avatar ?? 'bg-zinc-600 text-white'}`}
                      />
                      <div className="min-w-0 flex-1 text-left">
                        <div className="flex items-center justify-between gap-2">
                          <StrongText className="truncate">{doctor.name}</StrongText>
                          <span
                            className={`consultations-provider-status ${
                              bookable
                                ? 'consultations-provider-status-available'
                                : 'consultations-provider-status-unavailable'
                            }`}
                          >
                            {bookable ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                        <p className={`mt-0.5 text-xs/5 ${TEXT_SUBTLE}`}>
                          {doctor.specialty} · Next slot {doctor.nextSlot}
                        </p>
                        {!doctor.available ? (
                          <p className={`mt-1 text-[11px]/4 ${TEXT_FAINT}`}>Unavailable today</p>
                        ) : !matchesTimeSlot && consultForm.timeSlot ? (
                          <p className={`mt-1 text-[11px]/4 ${TEXT_FAINT}`}>
                            No opening at {consultForm.timeSlot}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Button>
                )
              })
            ) : (
              <p className={`text-sm/6 ${TEXT_SUBTLE}`}>
                No providers match the selected doctor type.
              </p>
            )}
          </div>
        </SurfaceCard>
      </section>

      <section className="consultations-appointments-grid">
        <SurfaceCard as="article">
          <div className="flex items-center justify-between gap-3">
            <Subheading variant="dashboard">Upcoming appointments</Subheading>
            <Badge variant={upcoming.length > 0 ? 'green' : 'zinc'} >
              {upcoming.length}
            </Badge>
          </div>

          {upcoming.length > 0 ? (
            <div className="mt-4 space-y-3">
              {upcoming.map((consultation) => {
                const colors = specialtyForDoctorId(consultation.doctorId)
                return (
                  <article key={consultation.id} className="consultations-appointment-row">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                        <VideoCameraIcon className="size-4" />
                      </span>
                      <div className="min-w-0">
                        <StrongText>{consultation.doctorName}</StrongText>
                        <p className={`mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs/5 ${TEXT_SUBTLE}`}>
                          <span className="inline-flex items-center gap-1">
                            <CalendarDaysIcon className="size-3.5" />
                            {consultation.date}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <ClockIcon className="size-3.5" />
                            {consultation.time}
                          </span>
                        </p>
                        <div className="mt-2">
                          <Badge variant={colors?.badge ?? 'zinc'}>
                            {consultation.reason}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-start">
                      {consultation.joinUrl ? (
                        <a
                          href={consultation.joinUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5"
                        >
                          Join call
                          <ArrowTopRightOnSquareIcon className="size-3.5" />
                        </a>
                      ) : (
                        <Badge variant="zinc">
                          No link yet
                        </Badge>
                      )}
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState
                icon={CalendarDaysIcon}
                title="No upcoming appointments"
                description="Choose a provider and book a new consultation."
                titleClassName={TEXT_SECONDARY}
                descriptionClassName={TEXT_SUBTLE}
                iconClassName={TEXT_FAINT}
              />
            </div>
          )}
        </SurfaceCard>

        <SurfaceCard as="article">
          <div className="flex items-center justify-between gap-3">
            <Subheading variant="dashboard">Past consultations</Subheading>
            <Badge variant="zinc">{past.length}</Badge>
          </div>

          {past.length > 0 ? (
            <div className="mt-4 space-y-3">
              {past.map((consultation) => {
                const colors = specialtyForDoctorId(consultation.doctorId)
                return (
                  <SurfaceRow key={consultation.id}>
                    <div className="flex items-start gap-3">
                      <Avatar
                        initials={doctorInitials(consultation.doctorName)}
                        className={`size-8 ${colors?.avatar ?? 'bg-zinc-600 text-white'}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <StrongText>{consultation.doctorName}</StrongText>
                            <p className={`text-xs/5 ${TEXT_SUBTLE}`}>
                              {consultation.date} · {consultation.time}
                            </p>
                          </div>
                          <Badge variant={colors?.badge ?? 'zinc'}>
                            {consultation.reason}
                          </Badge>
                        </div>
                        {consultation.notes && (
                          <p className={`mt-2 text-sm/6 ${TEXT_SECONDARY}`}>{consultation.notes}</p>
                        )}
                      </div>
                    </div>
                  </SurfaceRow>
                )
              })}
            </div>
          ) : (
            <p className={`mt-4 text-sm/6 ${TEXT_SUBTLE}`}>No completed consultations yet.</p>
          )}
        </SurfaceCard>
      </section>
    </div>
  )
}
